import mapboxgl from 'mapbox-gl';
import distance from '@turf/distance';
import bearing from '@turf/bearing';
import { bearingToAzimuth } from '@turf/helpers'
import { labelFormat } from './label-format.js';

/**
 * @typedef {import('@turf/helpers').Units} Units
 * @typedef {{
 *  units?: Units
 *  labelFormat?: (n: number) => string
 *  symbolLayout?: import('mapbox-gl').SymbolLayout
 *  symbolPaint?: import('mapbox-gl').SymbolPaint
 *  lineLayout?: import('mapbox-gl').LineLayout
 *  linePaint?: import('mapbox-gl').LinePaint
 *  markerCSS?: Partial<CSSStyleDeclaration>
 * 	invisible?: boolean
 * }} RulerControlOptions
 */

/**
 * @type {{
 * 	units: Units
 *  labelFormat: (n: number) => string
 * }}
 */
const defaults = {
    units: 'kilometers',
    labelFormat,
};

export default class RulerControl {
    /**
   * @param {RulerControlOptions} options
   */
    constructor(options = {}) {

        this.options = { ...defaults, ...options };
        this.container = this.controlContainer('mapbox-ctrl-ruler');
        this.isActive = false;
        /** @type {[number, number][]} */
        this.coordinates = [];
        /** @type {import('mapbox-gl').Marker[]} */
        this.markers = [];
        /** @type {HTMLButtonElement | null} */
        this.button = null;
        if (!this.options.invisible) {
            this.button = this.controlButton({
                title: 'Ruler',
                icon: this.ruler,
                onClick: () => this.onControlButtonClick(),
            });
        }
    }
    /**
     * Create mapbox control container
     * @param {string} className
     */
    ruler = this.parseSVG(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
    <rect fill="none" height="24" width="24"/>
    <path d="M20,6H4C2.9,6,2,6.9,2,8v8c0,1.1,0.9,2,2,2h16c1.1,0,2-0.9,2-2V8C22,6.9,21.1,6,20,6z M20,16H4V8h3v3c0,0.55,0.45,1,1,1h0 c0.55,0,1-0.45,1-1V8h2v3c0,0.55,0.45,1,1,1h0c0.55,0,1-0.45,1-1V8h2v3c0,0.55,0.45,1,1,1h0c0.55,0,1-0.45,1-1V8h3V16z"/>
</svg>
`);
    controlContainer(className) {
        const container = document.createElement('div');
        container.classList.add('mapboxgl-ctrl', 'mapboxgl-ctrl-group', className);
        return container;
    }
    /**
     * Create mapbox control button
     * @param {Object} options
     * @param {string=} options.title
     * @param {Node=} options.icon
     * @param {string=} options.textContent
     * @param {boolean=} options.disabled
     * @param {boolean=} options.hidden
     * @param {string=} options.className
     * @param {() => void=} options.onClick
     */
    controlButton(options = {}) {
        const button = document.createElement('button');
        button.type = 'button';
        if (options.title) {
            button.title = options.title;
        }
        if (options.icon) {
            button.appendChild(options.icon);
        }
        if (options.textContent) {
            button.textContent = options.textContent;
        }
        if (options.disabled) {
            button.disabled = true;
        }
        if (options.hidden) {
            button.hidden = true;
        }
        if (options.className) {
            button.classList.add(options.className);
        }
        if (options.onClick) {
            button.addEventListener('click', () => {
                if (!options.onClick) return;
                options.onClick();
            });
        }
        return button;
    }
    /** @type {import('mapbox-gl').LineLayer & { source: string }} */
    lineLayer = {
        id: 'mapbox-control-ruler-line',
        type: 'line',
        source: 'mapbox-control-ruler-lines',
        layout: {},
        paint: {
            'line-color': '#263238',
            'line-width': 2,
        },
    };

    /** @type {import('mapbox-gl').SymbolLayer & { source: string }} */
    symbolLayer = {
        id: 'mapbox-control-ruler-symbol',
        type: 'symbol',
        source: 'mapbox-control-ruler-points',
        layout: {
            'text-field': '{text}',
            'text-font': ['Roboto Medium'],
            'text-anchor': 'top',
            'text-size': 12,
            'text-offset': [0, 0.8],
        },
        paint: {
            'text-color': '#263238',
            'text-halo-color': '#fff',
            'text-halo-width': 1,
        },
    };
    parseSVG(string) {
        return /** @type SVGElement */ ((new DOMParser().parseFromString(string, 'image/svg+xml')).firstChild);
    }
    onControlButtonClick() {
        if (this.isActive) {
            this.deactivate();
        } else {
            this.activate();
        }
    }

    draw = () => {
        if (!this.map) throw Error('map is undefined');

        this.map.addSource(this.lineLayer.source, {
            type: 'geojson',
            data: this.asLine(),
        });

        this.map.addSource(this.symbolLayer.source, {
            type: 'geojson',
            data: this.asPoints(),
        });

        this.map.addLayer({
            ...this.lineLayer,
            layout: {
                ...this.lineLayer.layout,
                ...this.options.lineLayout,
            },
            paint: {
                ...this.lineLayer.paint,
                ...this.options.linePaint,
            },
        });

        this.map.addLayer({
            ...this.symbolLayer,
            layout: {
                ...this.symbolLayer.layout,
                ...this.options.symbolLayout,
            },
            paint: {
                ...this.symbolLayer.paint,
                ...this.options.symbolPaint,
            },
        });
    };

    activate() {
        if (!this.map) throw Error('map is undefined');
        this.isActive = true;
        this.markers = [];
        this.coordinates = [];
        this.map.getCanvas().style.cursor = 'crosshair';
        this.draw();
        this.map.on('click', this.mapClickListener);
        this.map.on('style.load', this.draw);
        this.map.fire('ruler.on');
        if (this.button) {
            this.button.classList.add('-active');
        }
    }

    deactivate() {
        if (!this.map) throw Error('map is undefined');
        this.isActive = false;
        this.map.getCanvas().style.cursor = '';
        // remove layers, sources and event listeners
        this.map.removeLayer(this.lineLayer.id);
        this.map.removeLayer(this.symbolLayer.id);
        this.map.removeSource(this.lineLayer.source);
        this.map.removeSource(this.symbolLayer.source);
        this.markers.forEach((m) => m.remove());
        this.map.off('click', this.mapClickListener);
        this.map.off('style.load', this.draw);
        this.map.fire('ruler.off');
        if (this.button) {
            this.button.classList.remove('-active');
        }
    }

    /**
    * @param {import('mapbox-gl').MapMouseEvent} event
    */
    mapClickListener = (event) => {
        if (!this.map) throw Error('map is undefined');
        this.addCoordinate([event.lngLat.lng, event.lngLat.lat]);
    };

    /**
     * @param {[number, number]} coordinate - [lng, lat] of new point
     */
    addCoordinate(coordinate) {
        if (!this.map) throw Error('map is undefined');
        if (!this.isActive) throw Error('ruler is not active');
        this.coordinates.push(coordinate);
        const markerElement = this.newMarkerElement();
        const marker = new mapboxgl.Marker({ element: markerElement, draggable: true })
            .setLngLat(coordinate)
            .addTo(this.map);
        this.markers.push(marker);
        this.updateSource();
        const markerIndex = this.markers.length - 1;
        marker.on('drag', () => {
            const lngLat = marker.getLngLat();
            this.coordinates[markerIndex] = [lngLat.lng, lngLat.lat];
            this.updateSource();
        });
    }

    updateSource() {
        if (!this.map) throw Error('map is undefined');
        this.map.fire('ruler.change', { coordinates: this.coordinates });
        const lineSource = /** @type {import('mapbox-gl').GeoJSONSource} */(this.map.getSource(this.lineLayer.source));
        const symbolSource = /** @type {import('mapbox-gl').GeoJSONSource} */(this.map.getSource(this.symbolLayer.source));
        lineSource.setData(this.asLine());
        symbolSource.setData(this.asPoints());
    }

    newMarkerElement() {
        const node = document.createElement('div');
        node.style.width = '12px';
        node.style.height = '12px';
        node.style.borderRadius = '50%';
        node.style.background = '#fff';
        node.style.boxSizing = 'border-box';
        node.style.border = '2px solid #263238';

        if (this.options.markerCSS) {
            Object.entries(this.options.markerCSS).forEach(([key, value]) => {
                node.style.setProperty(key, String(value));
            });
        }

        return node;
    }

    /**
    * @returns {import('geojson').Feature<import('geojson').LineString>}
    */
    asLine() {
        return {
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'LineString',
                coordinates: this.coordinates,
            },
        };
    }

    /**
    * @returns {import('geojson').FeatureCollection<import('geojson').Point>}
    */
    asPoints() {
        let sum = 0;
        let bb = 0;
        return {
            type: 'FeatureCollection',
            features: this.coordinates.map((coordinate, index) => {
                if (index > 0) {
                    sum += distance(this.coordinates[index - 1], coordinate, { units: this.options.units });
                    bb = bearingToAzimuth(Math.floor(bearing(this.coordinates[index - 1], coordinate)));

                }

                return {
                    type: 'Feature',
                    properties: {
                        text: `${this.options.labelFormat(sum)},${bb}°`,
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: coordinate,
                    },
                };
            }),
        };
    }

    /**
     * @param {import('mapbox-gl').Map} map
     * @returns {HTMLElement}
     */
    onAdd(map) {
        this.map = map;
        if (this.button) {
            this.container.appendChild(this.button);
        }
        return this.container;
    }

    onRemove() {
        if (this.isActive) this.deactivate();
        this.map?.off('click', this.mapClickListener);
        this.container.parentNode?.removeChild(this.container);
    }
}